import {
  createFileRoute,
  Outlet,
  redirect,
  useChildMatches,
  useNavigate,
} from '@tanstack/react-router'
import { AssetList } from '@/features/hr-assets'
import type { AssetListParams } from '@/features/hr-assets/api'
import { hasGrant } from '@/lib/me'

/**
 * Route shell for the asset register, following
 * `routes/_authenticated/hr/org.tsx`.
 *
 * The grant guard is presentation only. The API enforces `asset.view` on every
 * asset endpoint and the module gate refuses the whole `/api/hr` prefix for a
 * group whose plan does not include HR, so a stale grant still 403s
 * server-side; this only keeps a user without the grant out of a screen they
 * cannot use.
 *
 * Per the route-shell rule in `apps/company/AGENTS.md`, this file is a route
 * definition, a guard and a render. The screen lives in
 * `src/features/hr-assets/`.
 *
 * Filters and search live in the URL search params — a route concern, so a
 * filtered register is linkable and survives a reload, following
 * `employees.index.tsx`.
 *
 * ## Why this renders an Outlet
 *
 * `assets.$assetId.tsx` is a CHILD of this route in flat file routing, so
 * without an Outlet it never mounts and the detail URL silently rendered this
 * list instead. Same shape and same fix as `leave.tsx` and `expenses.tsx`.
 */
export const Route = createFileRoute('/_authenticated/hr/assets')({
  validateSearch: (search: Record<string, unknown>): AssetListParams => ({
    q: typeof search.q === 'string' ? search.q : undefined,
    status:
      typeof search.status === 'string' ? (search.status as AssetListParams['status']) : undefined,
    category_id: typeof search.category_id === 'string' ? search.category_id : undefined,
    create: search.create === '1' ? '1' : undefined,
  }),
  beforeLoad: ({ context }) => {
    if (!hasGrant(context.queryClient, 'asset.view')) {
      throw redirect({ to: '/' })
    }
  },
  component: HrAssetsRoute,
})

function HrAssetsRoute() {
  const navigate = useNavigate()
  const params = Route.useSearch()
  const children = useChildMatches()

  if (children.length > 0) {
    return <Outlet />
  }

  return (
    <AssetList
      params={params}
      onParamsChange={(next) => void navigate({ to: '/hr/assets', search: next, replace: true })}
    />
  )
}
